import { supabase, SUPABASE_PUBLISHABLE_KEY } from "@/shared/integrations/supabase/client";
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { captureEdgeFailure } from "@/shared/integrations/sentry/reportEdgeFailure";
import {
  uploadAssetContextPhoto,
  uploadAssetNameplatePhoto,
} from "@/shared/lib/firebaseStorage";
import { resizeImage } from "@/shared/lib/imageProcessing";

export interface NewAssetInput {
  propertyId: string;
  categoryId: string;
  name: string;
  zoneId?: string | null;
  notes?: string;
  nameplateFile?: File | null;
  photoFiles?: File[];
}

interface UploadAssetsResult {
  temp_id: string;
  success: boolean;
  action: "created" | "idempotent" | "error";
  database_id?: string;
  error?: string;
}

/**
 * Creates an asset exactly the way the Flutter field app does: photos are
 * uploaded to Firebase Storage first, then the shared `upload-assets` edge
 * function inserts the property_assets row, creates asset_documents rows for
 * each photo, and triggers the AI nameplate-extraction pipeline.
 *
 * Returns the new asset's database id.
 */
export async function createAsset(
  input: NewAssetInput,
  onProgress?: (message: string) => void
): Promise<string> {
  const { propertyId, categoryId, name, zoneId, notes, nameplateFile, photoFiles = [] } = input;

  const userId = firebaseAuth.currentUser?.uid;
  if (!userId) throw new Error("You must be signed in to create an asset");

  // Mobile uses the scanned QR id as the storage folder; web creation has no
  // QR, so the asset's ref_id (temp id) plays that role.
  const tempId = crypto.randomUUID();

  let nameplateUrl: string | undefined;
  if (nameplateFile) {
    onProgress?.("Uploading nameplate photo…");
    const resized = await resizeImage(nameplateFile);
    const { downloadUrl } = await uploadAssetNameplatePhoto(propertyId, tempId, name, resized);
    nameplateUrl = downloadUrl;
  }

  const photoUrls: string[] = [];
  for (let i = 0; i < photoFiles.length; i++) {
    onProgress?.(`Uploading photo ${i + 1} of ${photoFiles.length}…`);
    const resized = await resizeImage(photoFiles[i]);
    const { downloadUrl } = await uploadAssetContextPhoto(
      propertyId,
      tempId,
      name,
      i + 1,
      resized
    );
    photoUrls.push(downloadUrl);
  }

  onProgress?.("Creating asset…");
  const payload: Record<string, unknown> = {
    temp_id: tempId,
    property_id: propertyId,
    category_id: categoryId,
    name,
    asset_image_urls: photoUrls,
  };
  if (nameplateUrl) payload.nameplate_image_url = nameplateUrl;
  if (zoneId) payload.zone_id = zoneId;
  if (notes?.trim()) payload.notes = notes.trim();

  // upload-assets is deployed (from the field-app repo) with verify_jwt
  // enabled, so the gateway rejects our Firebase ID token. Authenticate the
  // same way the Flutter app does: anon key as bearer, user via x-user-id.
  const { data, error } = await supabase.functions.invoke("upload-assets", {
    body: { assets: [payload] },
    headers: {
      Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
      "x-user-id": userId,
    },
  });

  if (error) {
    captureEdgeFailure({ endpoint: "upload-assets", error });
    throw error;
  }

  const result: UploadAssetsResult | undefined = data?.results?.[0];
  if (!result?.success || !result.database_id) {
    throw new Error(result?.error || "Asset creation failed");
  }
  return result.database_id;
}
